import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Box,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { Users } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { format, endOfWeek, startOfWeek, eachDayOfInterval, isSameDay } from 'date-fns';
import { useBusiness } from '../../../context/BusinessContext';
import api from '../../../utils/api';
import { getStaffMemberDisplayPhoto } from '../../../utils/avatarConstants';
import DashboardCard from '../DashboardCard';

function StaffScheduleCard({ dashboardContext }) {
  const navigate = useNavigate();
  const { selectedBusiness } = useBusiness();
  const [schedule, setSchedule] = useState({
    weekDays: [],
    staffRows: [],
    loading: true,
    error: null
  });

  const businessId = selectedBusiness?.business?._id;
  const weekStart = useMemo(
    () => startOfWeek(dashboardContext?.weekStartMs ? new Date(dashboardContext.weekStartMs) : new Date(), { weekStartsOn: 1 }),
    [dashboardContext?.weekStartMs]
  );
  const weekEnd = useMemo(() => endOfWeek(weekStart, { weekStartsOn: 1 }), [weekStart]);
  const weekDays = useMemo(() => eachDayOfInterval({ start: weekStart, end: weekEnd }), [weekStart, weekEnd]);

  const getStaffMemberAvatar = (staffMember) => getStaffMemberDisplayPhoto(staffMember);

  const getStaffMemberInitials = (staffMember) => {
    const name = getStaffMemberName(staffMember);
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase() || 'ST';
  };

  const getStaffMemberName = (teamMember) => {
    if (!teamMember) return 'Unknown Staff';
    if (teamMember.name) return teamMember.name;
    if (teamMember.user) {
      return `${teamMember.user.firstName || ''} ${teamMember.user.lastName || ''}`.trim() || 'Unknown Staff';
    }
    return 'Unknown Staff';
  };

  const getStaffFirstName = (teamMember) => {
    const fullName = getStaffMemberName(teamMember);
    return fullName.split(' ').filter(Boolean)[0] || fullName;
  };

  const formatTimeRange = (startTime, endTime) => {
    return `${format(new Date(startTime), 'HH:mm')} - ${format(new Date(endTime), 'HH:mm')}`;
  };

  const fetchSchedule = useCallback(async () => {
    if (!businessId) return;

    try {
      setSchedule(prev => ({ ...prev, loading: true, error: null }));
      const [staffRes, shiftsRes] = await Promise.all([
        api.get(`/api/supplier/staff-members?businessId=${businessId}`),
        api.get(`/api/team-member-shifts/business/${businessId}`, {
          params: {
            start: weekStart.toISOString(),
            end: weekEnd.toISOString()
          }
        })
      ]);

      const staffMembers = staffRes.data?.staffMembers || [];
      const shifts = shiftsRes.data?.shifts || [];

      const userMatchIds = Array.isArray(dashboardContext?.userMatchIds)
        ? dashboardContext.userMatchIds.map((id) => String(id))
        : [];
      const isMyOverview = dashboardContext?.overviewScope === 'my';
      const visibleStaffMembers = isMyOverview && userMatchIds.length > 0
        ? staffMembers.filter((staff) => {
            const staffIds = [
              staff?._id,
              staff?.id,
              staff?.user?._id,
              staff?.user?.id,
            ].filter(Boolean);
            return staffIds.some((id) => userMatchIds.includes(String(id)));
          })
        : staffMembers;

      const staffRows = visibleStaffMembers.map((staff) => {
        const staffId = staff._id?.toString() || staff._id;
        const staffShifts = shifts.filter((shift) => {
          const shiftStaffId = shift.teamMember?._id?.toString() || shift.teamMember?.toString();
          return shiftStaffId === staffId;
        });

        const shiftsByDay = {};
        weekDays.forEach((day) => {
          const dayKey = format(day, 'yyyy-MM-dd');
          shiftsByDay[dayKey] = staffShifts
            .filter((shift) => isSameDay(new Date(shift.startTime), day))
            .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
        });

        return {
          staff,
          shiftsByDay
        };
      });

      setSchedule({ weekDays, staffRows, loading: false, error: null });
    } catch (error) {
      setSchedule({ weekDays: [], staffRows: [], loading: false, error: 'Failed to load staff schedule' });
    }
  }, [businessId, dashboardContext?.overviewScope, dashboardContext?.userMatchIds, weekDays, weekStart, weekEnd]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  return (
    <DashboardCard
      title="Staff Schedule This Week"
      loading={schedule.loading}
      error={schedule.error}
      isEmpty={!schedule.loading && schedule.staffRows.length === 0}
      emptyMessage="No shifts on the rota this week yet. Add your team and assign shifts to see the week at a glance."
      emptyIcon={Users}
      menuItems={[
        {
          label: 'Go to staff shifts',
          onClick: () => navigate('/supplier/schedules'),
        },
      ]}
      sx={{ height: '100%', minHeight: '400px' }}
    >
      {schedule.staffRows.length > 0 && (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              maxHeight: 360,
              '& .MuiTable-root': { borderCollapse: 'separate', borderSpacing: 0 },
              '& .MuiTableBody-root .MuiTableRow-root:hover': {
                backgroundColor: 'transparent'
              }
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 120, backgroundColor: 'background.paper' }}>
                    <Typography variant="caption" fontWeight={600}>Staff</Typography>
                  </TableCell>
                  {schedule.weekDays.map((day) => (
                    <TableCell key={format(day, 'yyyy-MM-dd')} sx={{ minWidth: 76, backgroundColor: 'background.paper', px: 1 }}>
                      <Typography variant="caption" fontWeight={600}>
                        {format(day, 'EEE')}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {schedule.staffRows.map(({ staff, shiftsByDay }) => {
                  const staffFirstName = getStaffFirstName(staff);
                  return (
                    <TableRow key={staff._id}>
                      <TableCell sx={{ py: 1, px: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar src={getStaffMemberAvatar(staff)} sx={{ width: 30, height: 30, fontSize: '0.75rem' }}>
                            {getStaffMemberInitials(staff)}
                          </Avatar>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {staffFirstName}
                          </Typography>
                        </Box>
                      </TableCell>
                      {schedule.weekDays.map((day) => {
                        const dayKey = format(day, 'yyyy-MM-dd');
                        const dayShifts = shiftsByDay[dayKey] || [];
                        return (
                          <TableCell key={dayKey} sx={{ verticalAlign: 'top', px: 1, py: 1 }}>
                            {dayShifts.length === 0 ? (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {dayShifts.slice(0, 2).map((shift) => (
                                  <Typography key={shift._id} variant="caption" sx={{ fontWeight: 500 }}>
                                    {formatTimeRange(shift.startTime, shift.endTime)}
                                  </Typography>
                                ))}
                                {dayShifts.length > 2 && (
                                  <Typography variant="caption" color="text.secondary">
                                    +{dayShifts.length - 2} more
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
      )}
    </DashboardCard>
  );
}

export default StaffScheduleCard;
